import React, { useEffect, useState } from "react";

import styles from "./OrderForm.module.scss";

import { GiCancel } from "react-icons/gi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getFormStep,
  getParties,
  getProcesses,
  updateFormStep,
  updateOrdersData,
} from "../../features/newOrder/newOrderSlice";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import Select from "react-select";
import { selectStyles } from "../../static/styles";
import { IFormInput, OptionsProps } from "../../types/order";
import { POSTFormData } from "../../services/axios.service";
import { route } from "../../static/route";
import { useNavigate } from "react-router-dom";
import ProcessForm from "./ProcessForm";

const OrderForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const parties = useAppSelector(getParties);
  const processes = useAppSelector(getProcesses);
  const formStep = useAppSelector(getFormStep);

  const [partiesOption, setPartiesOption] = useState<OptionsProps[]>([]);
  const [processOptions, setProcessOptions] = useState<OptionsProps[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  const methods = useForm<IFormInput>();
  const {
    control,
    handleSubmit,
    trigger,
    register,
    setValue,
    clearErrors,
    reset,
    formState: { errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "processes",
  });

  const handleNextStep = async () => {
    if (formStep === 1) {
      const isPartyValid = await trigger("party_id");
      if (isPartyValid) {
        const currentProcesses = methods.getValues("processes");
        if (!currentProcesses || currentProcesses.length === 0) {
          append({ photo: null, process_id: 0, qty: null });
        }

        dispatch(updateFormStep(formStep + 1));
      }
    }
  };

  const handleChangeParty = (party_id: number) => {
    if (party_id) {
      setValue("processes", []);
      setValue("party_id", party_id);
      clearErrors("party_id");
    }
  };

  const handleAppendNewProcess = () => {
    append({ photo: null, process_id: 0, qty: null });
  };

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    setIsLoading(true);
    setRequestError(null);

    try {
      if (data && data?.processes?.length > 0) {
        const formData: any = new FormData();

        formData.append("party_id", data.party_id);

        const processesPayload = data.processes.map((p) => ({
          process_id: p.process_id,
          qty: p.qty,
        }));
        formData.append("processes", JSON.stringify(processesPayload));

        // Append per-process images
        for (let i = 0; i < data.processes.length; i++) {
          const photoUri = data.processes[i].photo;
          if (photoUri) {
            const response = await fetch(photoUri);
            const blob = await response.blob();
            formData.append(`images[${i}]`, blob, `image_${i}.jpg`);
          }
        }

        await POSTFormData("/v1/worker/orders", formData)
          .then(async (response: any) => {
            if (response.data.status === "success") {
              await dispatch(updateFormStep(1));
              await dispatch(updateOrdersData(response.data.data || []));
              await reset();

              navigate(route.newOrderList);
            } else {
              setRequestError(response.data.message);
            }
          })
          .catch((error: any) => {
            setRequestError(error.data.message);
          });
      } else {
        setRequestError("At least 1 process is required in the new order.");
      }
    } catch (error) {
      setRequestError("Something went wrong. Please try again.");
    }

    setIsLoading(false);
  };

  useEffect(() => {
    const options: OptionsProps[] = [];
    parties?.forEach((party) => {
      options.push({ value: party.id, label: party.firm_name });
    });
    setPartiesOption(options);
  }, [parties]);

  useEffect(() => {
    const options: OptionsProps[] = [];
    processes?.forEach((process) => {
      options.push({ value: process.id, label: process.name });
    });
    setProcessOptions(options);
  }, [processes]);

  return (
    <div className={styles.OrderForm}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {formStep === 1 && (
            <>
              <div className="mt-6">
                <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
                  <Controller
                    {...register("party_id", { required: true })}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={partiesOption.find(
                          (val) => val.value === field?.value
                        )}
                        className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2/70 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                        styles={selectStyles}
                        placeholder={"Enter party name"}
                        options={partiesOption}
                        onChange={(val: any) =>
                          handleChangeParty(val?.value)
                        }
                      />
                    )}
                  />
                </div>
                {errors.party_id && errors.party_id.type === "required" && (
                  <span className="text-red-500">
                    Please select a party name
                  </span>
                )}
              </div>

              <div className="w-full flex justify-center">
                <button
                  className="mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                  type="button"
                  onClick={handleNextStep}
                >
                  Proceed
                </button>
              </div>
            </>
          )}

          {formStep === 2 && (
            <div className="w-full flex flex-col justify-center">
              {fields.map((item, index) => (
                <div key={item.id} className="relative">
                  {index > 0 && (
                    <GiCancel
                      onClick={() => remove(index)}
                      className="absolute z-10 shadow bg-white top-8 right-1 text-3xl text-primary-1 border-2 border-primary-1 p-1 rounded-full cursor-pointer"
                    />
                  )}
                  <ProcessForm
                    index={index}
                    processOptions={processOptions}
                  />
                </div>
              ))}

              <button
                className="mx-auto mt-4 shadow rounded-3xl text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-6 py-2"
                type="button"
                onClick={handleAppendNewProcess}
              >
                Add Process
              </button>

              {requestError !== null && (
                <span className="text-red-500 text-center mt-8">
                  {requestError}
                </span>
              )}

              <button
                className="mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Please wait..." : "Submit"}
              </button>
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
};
export default OrderForm;
